import { useState, useEffect } from 'react';

import { Button, Modal, Table, Typography, Tag } from "antd";
import _ from 'lodash';

import { ErrorResponse } from 'utils/types';
import { formatDate, getDisplayCompanyId } from 'utils/helpers';
import { Color, StatusCode } from 'utils/enums';
import notifier from 'utils/notifier';
import { ORGANIZATION_NAME, companyIdOptions } from 'utils/objects';

import AuthorizationDetailModal from 'components/Eligibility/MemberEligibilityDetailTables/AuthorizationHistoryTable/AuthorizationDetailModal';

import AuthorizationHistoryModalProps from './Proptypes';

import styles from './AuthorizationHistoryModal.module.scss';

const { Title } = Typography;

const AuthorizationHistoryModal = (props: AuthorizationHistoryModalProps) => {
  const [authHistoryCount, setAuthHistoryCount] = useState(0);
  const [authHistoryDataSource, setAuthHistoryDataSource] = useState<Array<Object>>([]);
  const [isAuthDetailModalVisible, setIsAuthDetailModalVisible] = useState(false);
  const [authorizationDetailModalDataSource, setAuthorizationDetailModalDataSource] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [companyName, setCompanyName] = useState('');

  const authorizationHistoryModalColumns = [
    {
      title: "IPA",
      key: "RmiCompanyId",
      dataIndex: "RmiCompanyId",
      render: (text: string) => getDisplayCompanyId(text)
    },
    {
      title: "AUTH NUMBER",
      key: "authNumber",
      dataIndex: "authNumber",
      render: (_: number, record: any) => <Button type='link' onClick={() => authHistoryDetailModal(record.RmiAuthNo, record.RmiCompanyId)}>{record.RmiAuthNo}</Button>
    },
    {
      title: "REFER TO PROVIDER",
      key: "RmiRevFullName",
      dataIndex: "RmiRevFullName",
    },
    {
      title: "STATUS",
      key: "RmiStatus",
      dataIndex: "RmiStatus",
      render: (_: number, record: any) => {
        return (
          <>
            { StatusCode[record.RmiStatus] === 'APPROVED' && <Tag color={Color.APPROVED}>APPROVED</Tag> }
            { StatusCode[record.RmiStatus] === 'MODIFIED' && <Tag color={Color.MODIFIED}>MODIFIED</Tag> }
            { StatusCode[record.RmiStatus] === 'DEFERRED' && <Tag color={Color.DEFERRED}>DEFERRED</Tag> }
            { StatusCode[record.RmiStatus] === 'CANCELLED' && <Tag color={Color.CANCELLED}>CANCELLED</Tag> }
            { StatusCode[record.RmiStatus] === 'EXPIRED' && <Tag color={Color.EXPIRED}>EXPIRED</Tag> }
            { StatusCode[record.RmiStatus] === 'DENIED' && <Tag color={Color.DENIED}>DENIED</Tag>}
            { StatusCode[record.RmiStatus] === 'PENDED' && <Tag color={Color.PENDED}>PENDED</Tag>}
            { StatusCode[record.RmiStatus] === 'REQUESTED' && <Tag style={{ "color": Color.REQUESTED_TEXT}}  color={Color.REQUESTED}>REQUESTED</Tag> }
            { StatusCode[record.RmiStatus] === 'SYSTEM_HOLD' && <Tag style={{ "color": Color.SYSTEM_HOLD_TEXT}} color={Color.SYSTEM_HOLD}>SYSTEM HOLD</Tag> }
          </>
        );
      }
    },
    {
      title: "EXPIRED DATE",
      key: "RmiExprDate",
      dataIndex: "RmiExprDate",
      render: (_: number, record: any) => formatDate(record.RmiExprDate, 'MM/DD/YYYY')
    },
  ];

  useEffect(() => {
    if (props.isAuthHistoryModalVisible) {
      authHistory();

      const companyInfo = companyIdOptions.find((val: { companyId: string | null; }) => val.companyId === sessionStorage.getItem('ipa'));
      if (companyInfo && companyInfo.name) setCompanyName(companyInfo!.name);
    }
  }, [props.isAuthHistoryModalVisible]);

  const isAuthDetailModalVisibleHandler = () => {
    setIsAuthDetailModalVisible(false);
  }

  const authHistoryDetailModal = (authNo: string, companyId: string) => {
    props.getPortalAuth(authNo, companyId, parseInt(sessionStorage.getItem('user_role')!))
    .then((data: any) => {
      setAuthorizationDetailModalDataSource(data);
      setIsAuthDetailModalVisible(true);
    })
    .catch((err: ErrorResponse) => {
      notifier.error(err.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
    });

    props.getAttachInfo(authNo, companyId)
      .then((response: any) => {
        setAttachments(response);
      }).catch((err: ErrorResponse) => {
        notifier.error(err.response.data.Message || 'Something went wrong, please contact a system administrator.');
      });
  }

  const authHistory = () => {
    const postAuthHistoryParams = {
      RmiCompanyId: props.eligibilityMemberInfo.RmiCompanyId,
      RmiEndDate: props.eligibilityMemberInfo.RmiEndDate,
      RmiMemberKeyId: props.eligibilityMemberInfo.RmiMembKeyId,
      RmiStartDate: props.eligibilityMemberInfo.RmiStartDate,
      UserId: sessionStorage.user_id,
      UserRole: sessionStorage.user_role
    }

    return props.postAuthHistory(postAuthHistoryParams)
      .then((response: any[]) => {
        setAuthHistoryCount(response.length);
        setAuthHistoryDataSource(_.orderBy(response, 'RmiExprDate', 'desc'));
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
      });
  }

  return (
    <>
      <Modal
        visible={props.isAuthHistoryModalVisible}
        onCancel={props.isAuthHistoryModalVisibleHandler}
        maskClosable={false}
        width="1000px"
        footer={[
          <>
            <div className="row pt-2">
              <div className="col-lg-24 col-md-24">
                <p className="float-left pt-2">&copy; {ORGANIZATION_NAME}, Inc., All Rights Reserved</p>
              </div>
            </div>
          </>
        ]}
      >
        <div className="container">
          <Title level={3}>
          <div>{companyName}</div>
          <div> Authorization History</div>
          </Title>
        </div>
        <div className={styles['authorization-heading']}>
          <div className="column">
            <dl>
              <dt>Member Name:</dt>
              <dd>{props.eligibilityMemberInfo.RmiLastName}, {props.eligibilityMemberInfo.RmiFirstName}</dd>
            </dl>
          </div>
          <div className="column">
            <dl>
              <dt>Member ID:</dt>
              <dd>{props.eligibilityMemberInfo.RmiMembId}</dd>
            </dl>
          </div>
          <div className="column">
            <dl>
              <dt>Count:</dt>
              <dd>{authHistoryCount}</dd>
            </dl>
          </div>
        </div>
        <Table
          columns={authorizationHistoryModalColumns}
          dataSource={authHistoryDataSource}
          pagination={{ position: ["bottomCenter"], hideOnSinglePage: true }}
          loading={props.isPosting}
        />
      </Modal>
      <AuthorizationDetailModal
        attachments={attachments}
        authorizationDetailModal={authorizationDetailModalDataSource}
        isModalVisible={isAuthDetailModalVisible}
        isModalVisibleHandler={isAuthDetailModalVisibleHandler}
        isAuthHistoryModalVisibleHandler={props.isAuthHistoryModalVisibleHandler}
        isFetchingAuth={props.isFetchingAuth}
      />
    </>
  );
};

export default AuthorizationHistoryModal;
